import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { apiResponse } from 'src/app/models/apiResponse.model';
import { ApiFilter, DefaultFilter, FieldsSelection, OrderByConfig } from 'src/app/models/apiParams.model';
import {
    ClaimAssessment,
    ClaimAssign,
    ClaimFinish,
    ClaimReject,
    ClaimUpdate,
    ClaimContactLogUpdate,
    ClaimCommentUpdate,
    ClaimProposalUpdate,
    ClaimContactLogCreate,
    ClaimProposalCreate,
    ClaimCommentCreate
} from 'src/app/models/claim-request.model';
import { BaseApiService } from '../base/base-api.service';

/**
 * Claim Service - Handles claims, assessments, assignments, proposals,
 * contact logs, comments, and related claim entities
 */
@Injectable({ providedIn: 'root' })
export class ClaimService extends BaseApiService {
    private readonly claimPath = '/claim/v1/';

    constructor(http: HttpClient) {
        super(http);
    }

    /*-------------------------
    == CLAIM TYPE CALLS ==
    -------------------------*/

    getDescriptionTypes(limit: number, page: number, orderBy?: string | OrderByConfig[] | null, filter?: string | ApiFilter | null, fields?: FieldsSelection | null, defaultFilter?: DefaultFilter | null): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.claimPath}category-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined });
    }

    getCommentTypes(limit: number, page: number, orderBy?: string | OrderByConfig[] | null, filter?: string | ApiFilter | null, fields?: FieldsSelection | null, defaultFilter?: DefaultFilter | null): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.claimPath}comment-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined });
    }

    getClaimResultTypes(limit: number, page: number, orderBy?: string | OrderByConfig[] | null, filter?: string | ApiFilter | null, fields?: FieldsSelection | null, defaultFilter?: DefaultFilter | null): Observable<apiResponse> {
        return this.getAll<apiResponse>(`${this.claimPath}result-type/`, { limit, page, orderBy: orderBy ?? undefined, filter: filter ?? undefined, fields: fields ?? undefined, defaultFilter: defaultFilter ?? undefined });
    }

    /*-------------------------
    == CLAIM CALLS ==
    -------------------------*/

    getClaims(limit: number, page: number, orderBy: string | OrderByConfig[], filter: string | ApiFilter, fields: FieldsSelection, defaultFilter: DefaultFilter): Observable<apiResponse> {
        return this.getAll<apiResponse>(this.claimPath, { limit, page, orderBy, filter, fields, defaultFilter });
    }

    getClaim(id: number, fields?: FieldsSelection): Observable<apiResponse> {
        return this.getById<apiResponse>(`${this.claimPath}`, id, { fields });
    }

    update(id: number, data?: ClaimUpdate): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.claimPath}`, id, data, 'json');
    }

    deleteClaim(id: number): Observable<apiResponse> {
        return super.delete<apiResponse>(`${this.claimPath}`, id);
    }

    /*-------------------------
    == CLAIM ACTION CALLS ==
    -------------------------*/

    assessment(id: number, data: ClaimAssessment): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.claimPath}${id}/assessment`, data, 'json');
    }

    assign(id: number, data: ClaimAssign): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.claimPath}${id}/assign`, data, 'json');
    }

    finish(id: number, data?: ClaimFinish): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.claimPath}${id}/finish`, data, 'json');
    }

    reject(id: number, data?: ClaimReject): Observable<apiResponse> {
        return this.putPath<apiResponse>(`${this.claimPath}${id}/reject`, data, 'json');
    }

    /*-------------------------
    == CLAIM CONTACT LOG CALLS ==
    -------------------------*/

    addContactLog(id: number, data: ClaimContactLogCreate): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.claimPath}${id}/contact-log`, data, 'json');
    }

    updateContactLog(id: number, contactLogId: number, data: ClaimContactLogUpdate): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.claimPath}${id}/contact-log/`, contactLogId, data, 'json');
    }

    /*-------------------------
    == CLAIM COMMENT CALLS ==
    -------------------------*/

    comment(id: number, data: ClaimCommentCreate): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.claimPath}${id}/comment`, data, 'json');
    }

    updateComment(id: number, commentId: number, data: ClaimCommentUpdate): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.claimPath}${id}/comment/`, commentId, data, 'json');
    }

    deleteComment(id: number, commentId: number): Observable<apiResponse> {
        return super.delete<apiResponse>(`${this.claimPath}${id}/comment/`, commentId);
    }

    /*-------------------------
    == CLAIM PROPOSAL CALLS ==
    -------------------------*/

    proposal(id: number, data: ClaimProposalCreate): Observable<apiResponse> {
        return this.post<apiResponse>(`${this.claimPath}${id}/proposal`, data, 'json');
    }

    updateProposal(id: number, proposalId: number, data: ClaimProposalUpdate): Observable<apiResponse> {
        return this.put<apiResponse>(`${this.claimPath}${id}/proposal/`, proposalId, data, 'json');
    }
}
